
import { defineComponent } from 'vue';
import gql from 'graphql-tag';
import { legalEntityUpdateAddress, legalEntityUpdateLogo, legalEntityUpdatePaymentPlanProcessingDetails } from '@/api/legal_entity';
import { Payment_Plan_Conditions, Period } from '@/schema/legal_entity/payment_plan/conditions';
import { Payment_Reference_Type } from '@/schema/legal_entity/processing_details';
import axios from 'axios';
import { COUNTRIES } from '@/constants';
import { Address } from '@/schema/address';
import Logging from '@/logging';

const PARTICIPANT = gql`
query ViewsLegalEntityDetail__participant($reference: uuid!) {
	risolto_participant(where: { reference: { _eq: $reference } }) {
		reference
		logical_id
	}
}
`;

const LEGAL_ENTITY = gql`
query ViewsLegalEntityDetail__legal_entity($participant: uuid!, $reference: uuid!) {
	risolto_legal_entity(
		where: {
			participant: { _eq: $participant }
			reference: { _eq: $reference }
		}
	) {
		reference
		registration_name
		company_id
		address
		processing_details
		custom_fields
	}
}
`;

export default defineComponent({
	name: 'LegalEntityDetail',
	apollo: {
		participant: {
			query: PARTICIPANT,
			variables() {
				return {
					reference: this.$route.params.participant
				};
			},
			update(data) {
				return data.risolto_participant[0];
			}
		},
		legalEntity: {
			query: LEGAL_ENTITY,
			variables() {
				return {
					participant: this.$route.params.participant,
					reference: this.$route.params.id
				};
			},
			update(data) {
				return data.risolto_legal_entity[0];
			}
		}
	},
	data() {
		return {
			looading: false,
			logoLoading: false,
			selectedFile: null,
			MIN_PERIOD: 2,
			loading: false,
			address_form: {
				company_name: '',
				street_name: '',
				street_number: '',
				zip_code: '',
				city: '',
				country: ''
			},
			payment_plan_form: {
				interest_rate_percentage: 0,
				interest_enabled_by_default: false,
				frequency: Period.MONTHLY,
				maximum_duration: 12,
				payment_reference_type: Payment_Reference_Type.OGM
			}
		};
	},
	computed: {
		periods() {
			return Object.values(Period);
		},
		paymentReferenceTypes() {
			return Object.values(Payment_Reference_Type);
		},
		countries() {
			let countries = COUNTRIES;
			// Translate country names
			countries.forEach(country => {
				country.name = this.$translate.iso_country_code(country.code);
			});
			// Sort by country name
			countries.sort((a, b) => {
				return a.name.localeCompare(b.name);
			});

			return countries;
		}
	},
	watch: {
		legalEntity() {
			const address: Address = this.legalEntity.address;
			const payment_plan_processing_details = this.legalEntity.processing_details.payment_plan;
			const payment_plan_conditions: Payment_Plan_Conditions = payment_plan_processing_details.conditions;

			this.address_form.company_name = address.company_name;
			this.address_form.street_name = address.street_name;
			this.address_form.street_number = address.street_number;
			this.address_form.zip_code = address.zip_code;
			this.address_form.city = address.city;
			this.address_form.iso_country_code = address.iso_country_code;

			this.payment_plan_form.frequency = payment_plan_conditions.frequency;
			this.payment_plan_form.maximum_duration = payment_plan_conditions.maximum_duration ? payment_plan_conditions.maximum_duration.duration : 12;
			this.payment_plan_form.payment_reference_type = payment_plan_processing_details.payment_reference_type;
		}
	},
	methods: {
		handleFileChange() {
			// console.log('document.getElementById: ', document.getElementById('file').files[0]);
			// @ts-ignore
			this.selectedFile = document.getElementById('file').files[0];
			this.handleAddAttachment();
		},
		handleAddAttachment() {
			if (this.logoLoading) {
				return;
			}

			this.logoLoading = true;

			const _this = this;
			// @ts-ignore
			if (!document.getElementById('file').files[0]) {
				this.$notify({
					title: this.$t('somethingWentWrong'),
					message: this.$t('selectAFile'),
					type: 'error'
				});
				return;
			} else {
				let payload = {
					participant_logical_id: this.participant.logical_id,
					extension: this.selectedFile.type.split('/')[1]
				};

				axios
					.post(`${process.env.VUE_APP_GET_ASSETS_SIGNED_URL_UPLOAD}`, payload, {
						headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
					})
					.then(({ data }) => {
						_this.signedUrl = data.signedUrl;

						var oFReader = new FileReader();
						oFReader.onload = function(e) {
							fetch(_this.signedUrl, {
								method: 'PUT',
								// @ts-ignore
								body: document.getElementById('file').files[0]
							})
								.then(async result => {
									Logging.log(result);

									const url = new URL(_this.signedUrl);
									const pathname = url.pathname.substring(1);

									await legalEntityUpdateLogo(
										_this.$route.params.participant,
										_this.$route.params.id,
										`https://${pathname}`
									);

									_this.$notify({
										title: _this.$t('success'),
										message: _this.$t('legal_entity.logo_updated'),
										type: 'success'
									});

									// refetch legal entity
									_this.$apollo.queries.legalEntity.refetch();

									_this.logoLoading = false;
								});
						};
						// @ts-ignore
						oFReader.readAsDataURL(document.getElementById('file').files[0]);
					});
			}
		},
		async updateAddress() {
			if (this.loading) return;
			try {
				this.loading = true;

				await legalEntityUpdateAddress(
					this.$route.params.participant,
					this.$route.params.id,
					this.address_form
				);

				this.$notify({
					title: this.$t('success'),
					message: this.$t('addressUpdated'),
					type: 'success'
				});
			} catch (e) {
				this.$notify({
					title: this.$t('warning'),
					message: this.$t('somethingWentWrong'),
					type: 'warning'
				});
			}

			this.loading = false;
		},
		async updateProcessingDetails() {
			if (this.loading) return;
			try {
				this.loading = true;

				await legalEntityUpdatePaymentPlanProcessingDetails(
					this.$route.params.participant,
					this.$route.params.id,
					this.payment_plan_form.frequency,
					this.payment_plan_form.maximum_duration,
					this.payment_plan_form.payment_reference_type
				);

				this.$notify({
					title: this.$t('success'),
					message: this.$t('processingDetailsUpdated'),
					type: 'success'
				});
			} catch (e) {
				this.$notify({
					title: this.$t('warning'),
					message: this.$t('somethingWentWrong'),
					type: 'warning'
				});
			}

			this.loading = false;
		}
	}
});
